import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../components/ui/Button';
import {
  GAME_HEIGHT,
  GAME_WIDTH,
  GRAVITY,
  INITIAL_PLATFORM_COUNT,
  JUMP_VELOCITY,
  MAX_FALL_SPEED,
  MOVE_SPEED,
  PLATFORM_COUNT,
  PLATFORM_GAP_MAX,
  PLATFORM_GAP_MIN,
  PLATFORM_WIDTH,
  PLAYER_SIZE,
  CLOUD_IMAGE_SRC,
  CLOUD_STAND_RATIO,
  CAT_IMAGE_SRC,
} from './constants';
import type { GamePhase, GameState, Platform, Player } from './types';

const BEST_SCORE_KEY = 'maru-cloud-jump-best';

function createPlayer(y: number): Player {
  return {
    x: GAME_WIDTH / 2 - PLAYER_SIZE / 2,
    y,
    vx: 0,
    vy: 0,
    width: PLAYER_SIZE,
    height: PLAYER_SIZE,
  };
}

function createPlatform(id: number, y: number): Platform {
  const width = PLATFORM_WIDTH + Math.random() * 24;
  const x = Math.random() * (GAME_WIDTH - width);
  return { id, x, y, width, height: width };
}

function createInitialPlatforms(counterStart: number): { platforms: Platform[]; nextId: number } {
  const platforms: Platform[] = [];
  let nextId = counterStart;
  let y = GAME_HEIGHT - 80;

  for (let i = 0; i < INITIAL_PLATFORM_COUNT; i += 1) {
    platforms.push(createPlatform(nextId, y));
    nextId += 1;
    y -= PLATFORM_GAP_MIN + Math.random() * (PLATFORM_GAP_MAX - PLATFORM_GAP_MIN);
  }

  return { platforms, nextId };
}

function getBestScore(): number {
  try {
    return Number(localStorage.getItem(BEST_SCORE_KEY) || 0);
  } catch {
    return 0;
  }
}

function saveBestScore(score: number) {
  try {
    localStorage.setItem(BEST_SCORE_KEY, String(score));
  } catch {
    // ignore
  }
}

function drawSky(ctx: CanvasRenderingContext2D) {
  const gradient = ctx.createLinearGradient(0, 0, 0, GAME_HEIGHT);
  gradient.addColorStop(0, '#87CEEB');
  gradient.addColorStop(0.55, '#B8E4FF');
  gradient.addColorStop(1, '#E8F6FF');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
}

function drawCloudPlatform(
  ctx: CanvasRenderingContext2D,
  cloudImage: HTMLImageElement | null,
  platform: Platform,
  screenY: number
) {
  if (!cloudImage?.complete || cloudImage.naturalWidth === 0) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(platform.x, screenY - 12, platform.width, 12);
    return;
  }

  const drawY = screenY - platform.height * CLOUD_STAND_RATIO;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(cloudImage, platform.x, drawY, platform.width, platform.height);
}

function drawCat(
  ctx: CanvasRenderingContext2D,
  catImage: HTMLImageElement | null,
  player: Player,
  screenY: number
) {
  const { x, width, height } = player;

  if (!catImage?.complete || catImage.naturalWidth === 0) {
    ctx.fillStyle = '#FF8C42';
    ctx.fillRect(x, screenY, width, height);
    return;
  }

  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(catImage, x, screenY, width, height);
}

function isPlayerOnPlatform(player: Player, platforms: Platform[]): boolean {
  const feet = player.y + player.height;
  return platforms.some(
    (platform) =>
      feet >= platform.y - 2 &&
      feet <= platform.y + 8 &&
      player.x + player.width * 0.65 > platform.x &&
      player.x + player.width * 0.35 < platform.x + platform.width
  );
}

function tryJump(state: GameState) {
  if (state.phase !== 'playing') return;
  if (!state.onGround && !isPlayerOnPlatform(state.player, state.platforms)) return;
  state.player.vy = JUMP_VELOCITY;
  state.onGround = false;
}

function ensurePlatforms(state: GameState) {
  const targetTop = state.cameraY - GAME_HEIGHT;
  let currentHighest = Math.min(...state.platforms.map((p) => p.y));

  while (currentHighest > targetTop || state.platforms.length < PLATFORM_COUNT) {
    const gap = PLATFORM_GAP_MIN + Math.random() * (PLATFORM_GAP_MAX - PLATFORM_GAP_MIN);
    currentHighest -= gap;
    state.platforms.push(createPlatform(state.platformIdCounter, currentHighest));
    state.platformIdCounter += 1;
  }

  state.platforms = state.platforms.filter((p) => p.y < state.cameraY + GAME_HEIGHT + 80);
}

function updateGame(state: GameState) {
  if (state.phase !== 'playing') return;

  const { player } = state;

  if (state.moveLeft) player.vx = -MOVE_SPEED;
  else if (state.moveRight) player.vx = MOVE_SPEED;
  else player.vx *= 0.82;

  player.vy = Math.min(player.vy + GRAVITY, MAX_FALL_SPEED);
  player.x += player.vx;
  player.y += player.vy;

  if (player.x + player.width < 0) player.x = GAME_WIDTH;
  if (player.x > GAME_WIDTH) player.x = -player.width;

  const prevBottom = player.y + player.height - player.vy;
  const currBottom = player.y + player.height;

  state.onGround = false;

  if (player.vy > 0) {
    for (const platform of state.platforms) {
      const onPlatform =
        currBottom >= platform.y &&
        prevBottom <= platform.y + 4 &&
        player.x + player.width * 0.7 > platform.x &&
        player.x + player.width * 0.3 < platform.x + platform.width;

      if (onPlatform) {
        player.y = platform.y - player.height;
        player.vy = 0;
        state.onGround = true;
        break;
      }
    }
  }

  if (state.jumpQueued) {
    tryJump(state);
    state.jumpQueued = false;
  }

  const targetCameraY = player.y - GAME_HEIGHT * 0.45;
  if (targetCameraY < state.cameraY) {
    state.cameraY = targetCameraY;
  }

  const heightScore = Math.max(0, Math.floor((GAME_HEIGHT - 80 - player.y) / 10));
  state.score = Math.max(state.score, heightScore);

  ensurePlatforms(state);

  if (player.y - state.cameraY > GAME_HEIGHT + 40) {
    state.phase = 'gameover';
    if (state.score > state.bestScore) {
      state.bestScore = state.score;
      saveBestScore(state.score);
    }
  }
}

function renderGame(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  cloudImage: HTMLImageElement | null,
  catImage: HTMLImageElement | null
) {
  drawSky(ctx);

  state.platforms.forEach((platform) => {
    const screenY = platform.y - state.cameraY;
    if (screenY > -platform.height && screenY < GAME_HEIGHT + platform.height) {
      drawCloudPlatform(ctx, cloudImage, platform, screenY);
    }
  });

  const playerScreenY = state.player.y - state.cameraY;
  drawCat(ctx, catImage, state.player, playerScreenY);
}

function createInitialState(phase: GamePhase = 'ready'): GameState {
  const groundY = GAME_HEIGHT - 80;
  const { platforms, nextId } = createInitialPlatforms(1);
  platforms[0] = {
    ...platforms[0],
    x: GAME_WIDTH / 2 - PLATFORM_WIDTH / 2,
    y: groundY,
    width: PLATFORM_WIDTH + 20,
    height: PLATFORM_WIDTH + 20,
  };

  return {
    phase,
    player: createPlayer(groundY - PLAYER_SIZE),
    platforms,
    cameraY: 0,
    score: 0,
    bestScore: getBestScore(),
    platformIdCounter: nextId,
    moveLeft: false,
    moveRight: false,
    onGround: true,
    jumpQueued: false,
  };
}

const CloudJumpGame: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<GameState>(createInitialState());
  const rafRef = useRef(0);
  const [uiPhase, setUiPhase] = useState<GamePhase>('ready');
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(getBestScore());
  const uiPhaseRef = useRef(uiPhase);
  const scoreRef = useRef(score);
  const bestScoreRef = useRef(bestScore);

  const cloudImageRef = useRef<HTMLImageElement | null>(null);
  const catImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const loadImage = (src: string, onLoad: (image: HTMLImageElement) => void, label: string) => {
      const image = new Image();
      image.src = src;
      image.onload = () => onLoad(image);
      image.onerror = () => console.error(`${label} 이미지를 불러오지 못했습니다.`);
    };

    loadImage(CLOUD_IMAGE_SRC, (image) => {
      cloudImageRef.current = image;
    }, '구름');
    loadImage(CAT_IMAGE_SRC, (image) => {
      catImageRef.current = image;
    }, '고양이');
  }, []);

  const queueJump = useCallback(() => {
    stateRef.current.jumpQueued = true;
  }, []);

  const startGame = useCallback(() => {
    stateRef.current = createInitialState('playing');
    setUiPhase('playing');
    setScore(0);
    setBestScore(stateRef.current.bestScore);
  }, []);

  const setMove = useCallback((left: boolean, right: boolean) => {
    stateRef.current.moveLeft = left;
    stateRef.current.moveRight = right;
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        stateRef.current.moveLeft = true;
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        stateRef.current.moveRight = true;
      }
      if (e.key === ' ') {
        e.preventDefault();
        if (stateRef.current.phase !== 'playing') {
          startGame();
        } else {
          queueJump();
        }
      }
      if (e.key === 'Enter') {
        if (stateRef.current.phase !== 'playing') {
          startGame();
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        stateRef.current.moveLeft = false;
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        stateRef.current.moveRight = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [queueJump, startGame]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let lastUiSync = 0;

    const loop = () => {
      const state = stateRef.current;
      updateGame(state);
      renderGame(ctx, state, cloudImageRef.current, catImageRef.current);

      const now = performance.now();
      if (now - lastUiSync > 120) {
        if (state.phase !== uiPhaseRef.current) {
          uiPhaseRef.current = state.phase;
          setUiPhase(state.phase);
        }
        if (state.score !== scoreRef.current) {
          scoreRef.current = state.score;
          setScore(state.score);
        }
        if (state.bestScore !== bestScoreRef.current) {
          bestScoreRef.current = state.bestScore;
          setBestScore(state.bestScore);
        }
        lastUiSync = now;
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'var(--Color-background-main, #F4F6FA)',
        fontFamily: 'var(--font-family-primary)',
        boxSizing: 'border-box',
        gap: 'var(--gap-level-4)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <h1
          style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: 600,
            color: 'var(--Color-text-strong)',
          }}
        >
          구름 위 고양이
        </h1>
        <p
          style={{
            margin: '8px 0 0',
            fontSize: '14px',
            color: 'var(--Color-text-normal)',
          }}
        >
          시즌 이벤트 — 구름 발판을 밟고 최대한 높이 올라가세요!
        </p>
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: GAME_WIDTH,
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(41, 48, 68, 0.15)',
          border: '3px solid white',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          width={GAME_WIDTH}
          height={GAME_HEIGHT}
          style={{ display: 'block', width: '100%', height: 'auto' }}
          aria-label="구름 위 고양이 게임 화면"
        />

        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
            display: 'flex',
            justifyContent: 'space-between',
            pointerEvents: 'none',
            color: 'white',
            fontWeight: 600,
            fontSize: '15px',
            textShadow: '0 1px 4px rgba(0,0,0,0.25)',
          }}
        >
          <span>{score}m</span>
          <span>최고 {bestScore}m</span>
        </div>

        {uiPhase !== 'playing' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              background: 'rgba(13, 13, 13, 0.45)',
              padding: '24px',
            }}
          >
            <div
              style={{
                fontSize: '22px',
                fontWeight: 700,
                color: 'white',
                textAlign: 'center',
              }}
            >
              {uiPhase === 'ready' ? '구름 위로 점프!' : '게임 오버'}
            </div>
            {uiPhase === 'gameover' && (
              <div style={{ color: 'white', fontSize: '16px' }}>기록: {score}m</div>
            )}
            <p
              style={{
                margin: 0,
                color: 'rgba(255,255,255,0.9)',
                fontSize: '13px',
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              ← → 또는 A D 로 이동 · 스페이스바로 점프
              <br />
              구름 위에 서 있을 때 점프할 수 있어요!
            </p>
            <Button variant="primary" size="medium" onClick={startGame}>
              {uiPhase === 'ready' ? '시작하기' : '다시 하기'}
            </Button>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: '12px',
          width: '100%',
          maxWidth: GAME_WIDTH,
        }}
      >
        <button
          type="button"
          aria-label="왼쪽 이동"
          disabled={uiPhase !== 'playing'}
          onTouchStart={() => setMove(true, false)}
          onTouchEnd={() => setMove(false, false)}
          onMouseDown={() => setMove(true, false)}
          onMouseUp={() => setMove(false, false)}
          onMouseLeave={() => setMove(false, false)}
          style={{
            flex: 1,
            height: 56,
            borderRadius: '14px',
            border: '1px solid var(--Color-line-normal)',
            background: 'var(--Color-background-normal)',
            fontSize: '24px',
            cursor: uiPhase === 'playing' ? 'pointer' : 'not-allowed',
            opacity: uiPhase === 'playing' ? 1 : 0.5,
          }}
        >
          ◀
        </button>
        <button
          type="button"
          aria-label="점프"
          disabled={uiPhase !== 'playing'}
          onTouchStart={queueJump}
          onMouseDown={queueJump}
          style={{
            flex: 1,
            height: 56,
            borderRadius: '14px',
            border: '1px solid var(--Color-line-normal)',
            background: 'var(--Color-fill-brand-color)',
            color: 'white',
            fontSize: '15px',
            fontWeight: 600,
            cursor: uiPhase === 'playing' ? 'pointer' : 'not-allowed',
            opacity: uiPhase === 'playing' ? 1 : 0.5,
          }}
        >
          점프
        </button>
        <button
          type="button"
          aria-label="오른쪽 이동"
          disabled={uiPhase !== 'playing'}
          onTouchStart={() => setMove(false, true)}
          onTouchEnd={() => setMove(false, false)}
          onMouseDown={() => setMove(false, true)}
          onMouseUp={() => setMove(false, false)}
          onMouseLeave={() => setMove(false, false)}
          style={{
            flex: 1,
            height: 56,
            borderRadius: '14px',
            border: '1px solid var(--Color-line-normal)',
            background: 'var(--Color-background-normal)',
            fontSize: '24px',
            cursor: uiPhase === 'playing' ? 'pointer' : 'not-allowed',
            opacity: uiPhase === 'playing' ? 1 : 0.5,
          }}
        >
          ▶
        </button>
      </div>
    </div>
  );
};

export default CloudJumpGame;
